import React, { useState, useEffect } from "react";
import { X, Check, ShoppingCart, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import { getAddonPlans, createAddonOrder, verifyAddonPayment } from "../apis/subscription";
import { loadScript } from "../utils/razorpay";

const AddonModal = ({ isOpen, onClose, onSuccess }) => {
  const [addonPlans, setAddonPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingAddon, setProcessingAddon] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  useEffect(() => {
    if (isOpen) {
      loadAddonPlans();
    }
  }, [isOpen]);

  const loadAddonPlans = async () => {
    try {
      setLoading(true);
      const response = await getAddonPlans();
      if (response.data.success) {
        setAddonPlans(response.data.data || []);
      }
    } catch (error) {
      console.error("Error loading addon plans:", error);
      toast.error("Failed to load add-on plans");
    } finally {
      setLoading(false);
    }
  };

  const getDisplayPrice = (plan, period) => {
    if (period === 'yearly') {
      const monthly = plan.monthly_amount || 300;
      const discount = plan.yearly_discount || 50;
      return monthly - discount;
    }
    return plan.monthly_amount || 300;
  };

  const getPaymentAmount = (plan, period) => {
    if (period === 'yearly') {
      const monthly = plan.monthly_amount || 300;
      const discount = plan.yearly_discount || 50;
      const discountedMonthly = monthly - discount;
      return discountedMonthly * 12;
    }
    return plan.monthly_amount || 300;
  };

  const handlePurchaseAddon = async (plan) => {
    setProcessingAddon(plan.id);

    try {
      await loadScript();

      const paymentAmount = getPaymentAmount(plan, billingPeriod);
      
      const orderResponse = await createAddonOrder(plan.id, billingPeriod);
      if (!orderResponse.data.success) {
        throw new Error(orderResponse.data.error || "Failed to create order");
      }

      const { order_id, amount, key_id } = orderResponse.data.data || {};

      const options = {
        key: key_id,
        amount: amount,
        currency: "INR",
        name: "Blutec PING",
        description: `Purchase ${plan.additional_flows || plan.flows || 3} Additional Flows`,
        order_id: order_id,
        handler: async function (response) {
          try {
            const verifyResponse = await verifyAddonPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });

            if (verifyResponse.data.success) {
              toast.success("Add-on activated successfully!");
              if (onSuccess) {
                onSuccess(verifyResponse.data.data);
              }
              onClose();
            } else {
              throw new Error(verifyResponse.data.error || "Payment verification failed");
            }
          } catch (error) {
            console.error("Payment verification error:", error);
            toast.error(error.response?.data?.error || "Payment verification failed");
          } finally {
            setProcessingAddon(null);
          }
        },
        prefill: {
          name: JSON.parse(localStorage.getItem("user") || "{}").name || "",
          email: JSON.parse(localStorage.getItem("user") || "{}").email || "",
        },
        theme: {
          color: "#048AAD",
        },
        modal: {
          ondismiss: function () {
            setProcessingAddon(null);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error("Purchase addon error:", error);
      toast.error(error.response?.data?.error || "Failed to initiate payment");
      setProcessingAddon(null);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Purchase Additional Flows</h2>
              <p className="text-sm text-gray-600 mt-1">Add more flows to your subscription</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <X size={24} className="text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-12 h-12 border-4 border-gray-900 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : addonPlans.length === 0 ? (
              <div className="text-center py-12">
                <AlertCircle size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No add-on plans available at the moment.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-center gap-4 mb-6">
                  <span className={`text-base font-bold ${billingPeriod === 'monthly' ? 'text-gray-900' : 'text-gray-500'}`}>
                    Monthly
                  </span>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly');
                    }}
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors cursor-pointer ${
                      billingPeriod === 'yearly' ? 'bg-gray-900' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform shadow-md ${
                        billingPeriod === 'yearly' ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className={`text-base font-bold ${billingPeriod === 'yearly' ? 'text-gray-900' : 'text-gray-500'}`}>
                    Yearly
                  </span>
                </div>

                <div className="space-y-4">
                  {addonPlans.map((plan) => {
                    const displayPrice = getDisplayPrice(plan, billingPeriod);
                    const paymentAmount = getPaymentAmount(plan, billingPeriod);
                    const isProcessing = processingAddon === plan.id;

                    return (
                      <motion.div
                        key={plan.id}
                        className="bg-white rounded-2xl border-2 border-purple-200 p-6 hover:border-purple-400 transition-all"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                              {plan.plan_display_name || plan.name || `${plan.additional_flows || plan.flows || 3} Additional Flows`}
                            </h3>
                            <div className="flex items-baseline gap-2 mb-4">
                              <span className="text-3xl font-bold text-gray-900">
                                ₹{displayPrice.toLocaleString()}
                              </span>
                              <span className="text-gray-600 font-semibold">
                                /{billingPeriod === 'yearly' ? 'year' : 'month'}
                              </span>
                            </div>
                            <ul className="space-y-2">
                              <li className="flex items-center gap-2 text-sm text-gray-700">
                                <Check size={16} className="text-emerald-600" />
                                <span>{plan.additional_flows || plan.flows || 3} additional flows</span>
                              </li>
                              <li className="flex items-center gap-2 text-sm text-gray-700">
                                <Check size={16} className="text-emerald-600" />
                                <span>{billingPeriod === 'yearly' ? '1 year validity' : '1 month validity'}</span>
                              </li>
                            </ul>
                          </div>
                          <div className="ml-6">
                            <motion.button
                              type="button"
                              onClick={() => handlePurchaseAddon(plan)}
                              disabled={isProcessing}
                              whileHover={!isProcessing ? { scale: 1.05 } : {}}
                              whileTap={!isProcessing ? { scale: 0.95 } : {}}
                              className={`px-6 py-3 rounded-xl font-bold text-white transition-all shadow-md ${
                                isProcessing
                                  ? "bg-gray-400 cursor-not-allowed"
                                  : "bg-purple-600 hover:bg-purple-700"
                              }`}
                            >
                              {isProcessing ? (
                                <div className="w-5 h-5 border-2 border-white/60 border-t-transparent rounded-full animate-spin mx-auto" />
                              ) : (
                                <>
                                  <ShoppingCart size={18} className="inline mr-2" />
                                  Purchase
                                </>
                              )}
                            </motion.button>
                            {paymentAmount && (
                              <p className="text-xs text-gray-500 mt-2 text-center">
                                Pay ₹{paymentAmount.toLocaleString()}
                              </p>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AddonModal;
